/**
 * Frappe API Client
 * Kommunikation mit Frappe Backend für Synchronisation
 */

import type { FrappeResponse, FrappeLoginResponse, Product, SalesInvoice, ZReport } from '../types';
import { authStorage, settingsStorage } from './storage';
import { getFrappeApiUrl } from '../config/frappe';

const DEFAULT_TIMEOUT = 10000; // 10 seconds

class FrappeClient {
  private baseUrl: string;
  private timeout: number;

  private formatFrappeDateTime(value?: string | Date | null): string {
    if (!value) value = new Date();
    const date = value instanceof Date ? value : new Date(value);
    const pad = (num: number) => num.toString().padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  constructor(baseUrl?: string, timeout: number = DEFAULT_TIMEOUT) {
    // Verwende übergebene URL, oder lade aus Konfiguration
    this.baseUrl = baseUrl || getFrappeApiUrl();
    this.timeout = timeout;
  }

  /**
   * Erstellt Authorization Header
   */
  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    const settings = settingsStorage.load();
    if (settings.frappe_api_key && settings.frappe_api_secret) {
      headers['Authorization'] = `token ${settings.frappe_api_key}:${settings.frappe_api_secret}`;
      return headers;
    }

    const token = authStorage.loadToken();
    if (token?.access_token) {
      headers['Authorization'] = `token ${token.access_token}`;
      return headers;
    }

    throw new Error(
      'Nicht authentifiziert. Bitte API Key & Secret in den Einstellungen hinterlegen.'
    );
  }

  /**
   * Führt API Request aus
   * Bei erfolgreichen Requests wird die Session automatisch verlängert
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    skipAuth: boolean = false
  ): Promise<FrappeResponse<T>> {
    if (!this.baseUrl) {
      throw new Error('Frappe API URL nicht konfiguriert');
    }

    const url = `${this.baseUrl.replace(/\/$/, '')}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options.headers
      };

      // Auth-Header nur hinzufügen, wenn nicht übersprungen (z.B. für Login)
      if (!skipAuth) {
        const authHeaders = this.getAuthHeaders();
        Object.assign(headers, authHeaders);
      }

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Bei erfolgreichen Requests: Session verlängern (Frappe macht das automatisch)
      // Aktualisiere expires_at im Token, um lokale Gültigkeit zu verlängern
      if (!skipAuth) {
        this.updateSessionExpiry();
      }

      return { data };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - Server antwortet nicht');
        }
        throw error;
      }
      throw new Error('Unbekannter Fehler bei API-Request');
    }
  }

  /**
   * Aktualisiert die Session-Ablaufzeit im Token
   * Wird nach jedem erfolgreichen API-Request aufgerufen
   */
  private updateSessionExpiry(): void {
    const token = authStorage.loadToken();
    if (token) {
      // Verlängere Session um 24 Stunden ab jetzt
      const updatedToken = {
        ...token,
        expires_at: Date.now() + 24 * 60 * 60 * 1000
      };
      authStorage.saveToken(updatedToken);
    }
  }

  /**
   * Login mit Username/Password
   * Login-Endpoint benötigt keinen Auth-Header
   *
   * Standard Frappe Login gibt zurück:
   * {
   *   "message": {
   *     "full_name": "...",
   *     "sid": "session-id-here"
   *   }
   * }
   *
   * Oder direkt:
   * {
   *   "message": {
   *     "full_name": "...",
   *     "sid": "session-id-here"
   *   }
   * }
   */
  async login(username: string, password: string): Promise<FrappeLoginResponse> {
    interface FrappeLoginPayload {
      message?: string;
      full_name?: string;
      sid?: string;
      api_key?: string;
      api_secret?: string;
    }

    const response = await this.request<FrappeLoginPayload>(
      '/api/method/login',
      {
        method: 'POST',
        body: JSON.stringify({
          usr: username,
          pwd: password
        })
      },
      true // skipAuth = true für Login
    );

    const payload = response.data;
    if (!payload) {
      throw new Error('Login fehlgeschlagen - Keine Antwort vom Server');
    }

    if (payload.message && payload.message !== 'Logged In') {
      throw new Error(payload.message);
    }

    return {
      full_name: payload.full_name || username,
      sid: payload.sid,
      api_key: payload.api_key,
      api_secret: payload.api_secret
    };
  }

  /**
   * Lädt alle Produkte
   */
  async getProducts(): Promise<Product[]> {
    interface FrappeItem {
      name: string;
      item_name?: string;
      item_code: string;
      description?: string;
      standard_rate?: number;
      stock_qty?: number;
      stock_uom?: string;
      image?: string;
      item_category?: string;
    }

    interface FrappeItemResponse {
      data?: FrappeItem[];
    }

    const response = await this.request<FrappeItemResponse | FrappeItem[]>(
      '/api/resource/Item?fields=["name", "item_name", "item_code", "description", "standard_rate", "stock_uom", "image", "item_category"]',
      {
        method: 'GET'
      }
    );

    // Frappe API gibt data als Objekt mit data Array zurück
    // Struktur: { data: [{ name, item_name, ... }, ...] }
    // Oder direkt als Array
    let items: FrappeItem[] = [];
    if (Array.isArray(response.data)) {
      items = response.data;
    } else if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      items = response.data.data;
    }

    // Mapping von Frappe Item zu Product
    return items.map((item: FrappeItem) => ({
      id: item.name, // Frappe name als ID
      name: item.item_name || item.name, // item_name oder name
      item_code: item.item_code,
      description: item.description,
      price: item.standard_rate || 0,
      stock: item.stock_qty, // Falls vorhanden
      unit: item.stock_uom,
      image_url: item.image ? `${this.baseUrl}${item.image}` : undefined,
      category: item.item_category // item_category statt item_group
    }));
  }

  /**
   * Synchronisiert einzelnes Produkt
   */
  async syncProduct(product: Product): Promise<Product> {
    const response = await this.request<Product>('/api/resource/Item', {
      method: 'POST',
      body: JSON.stringify(product)
    });

    if (!response.data) {
      throw new Error('Produkt-Synchronisation fehlgeschlagen');
    }

    return response.data;
  }

  /**
   * Erstellt eine Account Transaction
   * Gibt nur das 'name' Feld aus der Frappe Response zurück
   */
  async createAccountTransaction(
    transaction: {
      amount: number;
      transaction_ts: string;
      credit: string;
      debit: string;
    },
    testMode: boolean = false
  ): Promise<{ name: string }> {
    const transactionWithStatus = {
      ...transaction,
      docstatus: testMode ? 0 : 1
    };
    const response = await this.request<{ data: { name: string } }>(
      '/api/resource/Account Transaction',
      {
        method: 'POST',
        body: JSON.stringify(transactionWithStatus)
      }
    );

    if (!response.data?.data?.name) {
      throw new Error('Account Transaction Erstellung fehlgeschlagen');
    }

    return { name: response.data.data.name };
  }

  /**
   * Erstellt/Synchronisiert Sales Invoice
   * Gibt nur das 'name' Feld aus der Frappe Response zurück
   */
  async syncInvoice(
    invoice: SalesInvoice,
    settings: {
      revenue_account?: string;
      cash_account?: string;
      sumup_account?: string;
      payment_fees_account?: string;
      sumup_fee_percentage?: number;
      test_mode?: boolean;
    }
  ): Promise<{ name: string }> {
    // Erstelle Account Transactions nach erfolgreicher Invoice-Erstellung
    const revenueAccount = settings.revenue_account || 'ACC-4000';
    const cashAccount = settings.cash_account || 'ACC-2700';
    const sumupAccount = settings.sumup_account || 'ACC-2801';
    const paymentFeesAccount = settings.payment_fees_account || 'ACC-7790';
    const sumupFeePercentage = settings.sumup_fee_percentage || 1.39;

    const isCash = invoice.payments[0]?.type === 'cash';
    const isSumUp = invoice.payments[0]?.type !== 'cash';

    // Konvertiere CartItems zu Frappe Sales Invoice Items
    const items = invoice.items.map((item) => ({
      item: item.product.id,
      description: item.product.description,
      quantity: item.quantity,
      rate: item.price,
      amount: item.subtotal,
      a_ref: revenueAccount
    }));

    // Erstelle Frappe Sales Invoice Format
    const invoice_date = this.formatFrappeDateTime(invoice.created_at || new Date());
    const testMode = settings.test_mode || false;
    const frappeInvoice = {
      doc: {
        doctype: 'Sales Invoice', // only needed for frappe.client.submit approach
        customer: invoice.customer || 'C00272511',
        sale_items: items,
        net_total: invoice.total,
        grand_total: invoice.total,
        status: 'Received',
        invoice_created: invoice_date,
        invoice_due: invoice_date,
        payment_method: invoice.payments[0]?.type === 'cash' ? 'Bar' : 'SumUp',
        payment_reference: invoice.invoice_number,
        is_pos_sale: true //,
        //docstatus: testMode ? 0 : 1
      }
    };

    // const response = await this.request<{ data: { name: string } }>('/api/resource/Sales Invoice', {
    //   method: 'POST',
    //   body: JSON.stringify(frappeInvoice)
    // });
    const api_endpoint = testMode
      ? '/api/method/frappe.client.save'
      : '/api/method/frappe.client.submit';
    const response = await this.request<{ message: { name: string } }>(api_endpoint, {
      method: 'POST',
      body: JSON.stringify(frappeInvoice)
    });

    if (!response.data?.message?.name) {
      throw new Error('Rechnung-Synchronisation fehlgeschlagen');
    }

    const invoiceName = response.data.message.name;

    // // Haupttransaktion: Erlös gegen Zahlungsmittel
    // await this.createAccountTransaction(
    //   {
    //     amount: invoice.total,
    //     transaction_ts: invoice_date,
    //     credit: revenueAccount,
    //     debit: isCash ? cashAccount : sumupAccount
    //   },
    //   testMode
    // );
    //
    // // Bei SumUp-Zahlung: Zusätzliche Transaktion für Gebühren
    // if (isSumUp) {
    //   const feeAmount = invoice.total * (sumupFeePercentage / 100);
    //   await this.createAccountTransaction(
    //     {
    //       amount: feeAmount,
    //       transaction_ts: invoice_date,
    //       credit: sumupAccount,
    //       debit: paymentFeesAccount
    //     },
    //     testMode
    //   );
    // }

    return { name: invoiceName };
  }

  /**
   * Synchronisiert Z-Report
   * Gibt nur das 'name' Feld aus der Frappe Response zurück
   */
  async syncZReport(report: ZReport, testMode: boolean = false): Promise<{ name: string }> {
    // Erstelle Frappe Z-Report Format
    // sales ist eine Child Table, daher muss das Format für Frappe angepasst werden
    const startTime = this.formatFrappeDateTime(report.start_time) || report.start_time;
    const endTime = this.formatFrappeDateTime(report.end_time) || report.end_time;

    const frappeReport: Record<string, unknown> = {
      z_report_nr: report.report_number,
      z_report_start: startTime,
      z_report_end: endTime,
      sales_count: report.sales_count,
      total_sales: report.total_sales,
      bar_sales: report.cash_sales,
      card_sales: report.card_sales,
      z_report_posid: report.pos_id,
      docstatus: testMode ? 0 : 1
    };

    // Child Table: sales als Array von Objekten
    if (report.sales && report.sales.length > 0) {
      frappeReport.sales = report.sales.map((entry) => ({
        sales_invoice: entry.sales_invoice,
        sales_pos_invoice_number: entry.sales_pos_invoice_number
      }));
    }

    const response = await this.request<{ data: { name: string } }>('/api/resource/Z Report', {
      method: 'POST',
      body: JSON.stringify(frappeReport)
    });

    if (!response.data?.data?.name) {
      throw new Error('Z-Report Synchronisation fehlgeschlagen');
    }

    return { name: response.data.data.name };
  }

  /**
   * Prüft Verbindung zum Server
   */
  async ping(): Promise<boolean> {
    try {
      await this.request('/api/method/ping', { method: 'GET' });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Aktualisiert Base URL
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }
}

// Singleton Instance
export const frappeClient = new FrappeClient();

export default frappeClient;
